import type { ReactElement } from "react";

import { useEffect, useState } from "react";

import mark from "../design/assets/logo/silvererp-mark.svg";
import { SearchField, SidebarNav } from "../design-system";
import { UserFooter } from "./UserFooter";
import type { SessionView } from "../session/useSession";

/** Ein Untereintrag, wie die Spalte ihn anbietet: Kennung und Beschriftung. */
export interface RailArea<Id extends string = string> {
  id: Id;
  label: string;
}

/**
 * Ein **Modulplatz** im Menü der Spalte.
 *
 * Zweistufig sind `areas` die Untereinträge. **Einstufig** ist `areas` leer und
 * `area` der Bereich, in den der Platz selbst führt — die Verwaltung und das
 * Gerüst haben keine zweite Stufe.
 */
export interface RailModule<Id extends string = string> {
  /** Das Wort auf dem Modulplatz — frei; Position, Reihenfolge und Glyphe sind es nicht. */
  label: string;
  /** Die Glyphe der Vorlage. */
  glyph: string;
  areas: readonly RailArea<Id>[];
  area: Id | null;
}

/**
 * Die **Sidebar-Spalte**: erstes Flex-Kind des Gerüsts, `--sidebar-width` breit,
 * über die volle Fensterhöhe, Fläche `--bg-chrome`, und darin die vier Blöcke des
 * Rahmens (`FRAME.md`, „Sidebar"):
 *
 * | Block | Höhe | Inhalt |
 * | --- | --- | --- |
 * | Logo-Zeile | `--h-logo-row` | Signet · `SilverERP-Devtools` — ohne Menü und ohne Auslöser |
 * | Menüsuche | `--h-search-side` | `SearchField`, Platzhalter `Menü durchsuchen ...` |
 * | Navigation | flex 1, scrollt | Augenbraue `Navigation` · zweistufiges Menü (`SidebarNav`) |
 * | Benutzerfuß | auto | `UserFooter` — Avatar, Name, `chevrons-up-down` → Benutzermenü |
 *
 * **Keine rechte Trennlinie und kein Glas.** Die Kante zum Inhalt trägt die
 * abgerundete Inhaltsfläche daneben; der Weichzeichner, der hier einmal stand, war
 * eine hiesige Erfindung ohne Vorlage. Eingeklappt ist sie
 * `--sidebar-width-collapsed` schmal, der Übergang ist `--t-side`, und **nichts
 * umbricht**: Die Spalte lässt keinen Umbruch zu, jeder Block schneidet für sich
 * ab, die Beschriftungen blenden aus, und die Namen bleiben als Titel am Eintrag.
 *
 * **Die Spalte selbst schneidet nicht ab.** Das täte sie einmal zu viel: Das
 * Benutzermenü hängt an ihr, ist mindestens 236px breit und klappt über den
 * Inhalt — eingeklappt ist die Spalte 64px schmal, und ein `overflow: hidden` an
 * ihr schnitte es ab.
 *
 * **Welche** Bereiche sie zeigt, entscheidet sie nicht: Sie zeigt, was sie bekommt.
 * Die Modulordnung und die Rollenfilterung stehen in `navigation/modules.ts` und
 * sind eine Frage der Anzeige — die Rechteentscheidung trifft der Server bei jeder
 * Anfrage.
 */
export function NavRail<Id extends string>({
  modules,
  active,
  collapsed,
  onSelect,
  version,
  onOpenVersionHistory,
  onOpenCredentials,
  session,
}: {
  modules: readonly RailModule<Id>[];
  active: string;
  collapsed: boolean;
  onSelect: (id: Id) => void;
  /** Die laufende Fassung — die Spalte reicht sie an den Benutzerfuß durch. */
  version: string | null;
  onOpenVersionHistory: () => void;
  /** „Einstellungen ▸ Anmeldedaten ändern" — die Spalte reicht ihn durch. */
  onOpenCredentials: () => void;
  session: SessionView;
}): ReactElement {
  const [query, setQuery] = useState("");
  const { sections, toggle } = useOpenSections(modules, active);

  // Der Platz, auf dem der offene Bereich sitzt, und sein Untereintrag. Beide über
  // die **Beschriftung**, denn darüber redet der Baustein des Skills: Er kennt
  // keine Kennungen, sondern Wörter.
  const standing = modules.find(
    (one) => one.area === active || one.areas.some((area) => area.id === active),
  );
  const leaf = standing?.areas.find((area) => area.id === active)?.label;

  const hits = matching(modules, query);

  return (
    <nav
      aria-label="Bereiche"
      style={{
        width: collapsed ? "var(--sidebar-width-collapsed)" : "var(--sidebar-width)",
        flex: "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        // Über der Spalte rechts: Das Benutzermenü hängt an dieser Spalte und
        // klappt über den Inhalt.
        zIndex: 20,
        background: "var(--bg-chrome)",
        transition: "width var(--t-side)",
        whiteSpace: "nowrap",
      }}
    >
      <LogoRow collapsed={collapsed} />

      {/* Die Menüsuche filtert das **Menü** und nichts sonst — es gibt keinen Index
          über Revisionen, Befunde und Stellen. */}
      <div
        style={{
          flex: "none",
          paddingTop: 6,
          paddingBottom: 10,
          paddingLeft: 14,
          paddingRight: 14,
          overflow: "hidden",
        }}
      >
        <SearchField
          value={query}
          onChange={setQuery}
          // Eingeklappt bleibt nur die Glyphe: kein Platzhalter und kein
          // esc-Zeichen, die in 36px Breite ohnehin nur anstießen.
          placeholder={collapsed ? "" : "Menü durchsuchen ..."}
          showEsc={!collapsed}
          // `--h-search-side` ist 34px. Der Baustein nimmt seine Höhe als Zahl und
          // nicht als Ausdruck; der Wert steht deshalb hier und der Name daneben.
          height={34}
        />
      </div>

      <SidebarNav
        items={hits.map((one) => ({
          label: one.label,
          icon: one.glyph,
          // Ein einstufiger Platz bekommt **keine** Untereinträge. Der Baustein
          // des Skills verträgt das — er zeichnet dann nur die Gruppenzeile —,
          // zeigt daran aber weiter seinen Aufklappwinkel; das ist ein Mangel der
          // Vorlage und in `design/VENDOR.md` aufgeschrieben.
          children: one.areas.length === 0 ? undefined : one.areas.map((area) => area.label),
        }))}
        collapsed={collapsed}
        activeSection={standing?.label}
        active={leaf}
        openSections={sections}
        heading={query === "" ? "Navigation" : hits.length === 0 ? "Keine Treffer" : "Treffer"}
        onToggleSection={(label) => {
          const found = modules.find((one) => one.label === label);

          // Einstufig ist der Platz selbst der Bereich: Ein Klick darauf führt
          // dorthin und klappt nichts auf.
          if (found !== undefined && found.area !== null) {
            onSelect(found.area);
            return;
          }

          toggle(label);
        }}
        onSelect={(label, child) => {
          const found = modules
            .find((one) => one.label === label)
            ?.areas.find((area) => area.label === child);

          if (found !== undefined) {
            onSelect(found.id);
          }
        }}
      />

      <UserFooter
        session={session}
        collapsed={collapsed}
        // Die laufende Fassung und der Weg in die Neuerungen hängen seit #158 am
        // Benutzermenü: Wo es keine Mandanten gibt, bleibt der Platz des
        // Unit-Menüs leer, statt etwas anderes zu öffnen.
        version={version}
        onOpenVersionHistory={onOpenVersionHistory}
        onOpenCredentials={onOpenCredentials}
      />
    </nav>
  );
}

/**
 * Welche Gruppen offen sind.
 *
 * Aufgeklappt wird von Hand — und die Gruppe des Bereichs, in dem man **landet**,
 * klappt von selbst auf: bei einem Tiefenlink, über die Brotkrume, beim Sprung aus
 * den Befunden in den Strom. Ohne das stünde der aktive Untereintrag in einer
 * geschlossenen Gruppe und wäre nicht zu sehen. Zugeklappt bleibt sie danach, wenn
 * jemand sie zuklappt: Der Effekt hängt am Bereich und nicht an jedem Zeichnen.
 */
function useOpenSections<Id extends string>(
  modules: readonly RailModule<Id>[],
  active: string,
): { sections: string[]; toggle: (label: string) => void } {
  const standing =
    modules.find((one) => one.areas.some((area) => area.id === active))?.label ?? null;
  const [sections, setSections] = useState<string[]>(standing === null ? [] : [standing]);

  useEffect(() => {
    if (standing !== null) {
      setSections((current) => (current.includes(standing) ? current : [...current, standing]));
    }
  }, [standing]);

  return {
    sections,
    toggle: (label: string): void =>
      setSections(
        sections.includes(label) ? sections.filter((one) => one !== label) : [...sections, label],
      ),
  };
}

/**
 * Die Plätze, die zur Suche passen — Untereinträge gefiltert, ein Platz behalten,
 * wenn er selbst passt oder noch einen Untereintrag hat.
 *
 * Genau so filtert die Vorlage (`reference/AppShell.jsx`, `Sidebar`).
 */
function matching<Id extends string>(
  modules: readonly RailModule<Id>[],
  query: string,
): RailModule<Id>[] {
  if (query === "") {
    return [...modules];
  }

  const wanted = query.toLowerCase();

  return modules
    .map((one) => ({
      ...one,
      areas: one.areas.filter((area) => area.label.toLowerCase().includes(wanted)),
    }))
    .filter((one) => one.areas.length > 0 || one.label.toLowerCase().includes(wanted));
}

/**
 * Die **Logo-Zeile**: Signet und Produktname, sonst nichts.
 *
 * Sie steht an der Stelle, an der die Vorlage Mandant und Unit zeigt. Mandanten
 * gibt es hier nicht — und wo ein Produkt keine hat, **fällt der Platz weg**:
 * kein Menü, kein Auslöser, kein `chevrons-up-down`. Ein Knopf an der Stelle des
 * Unit-Menüs, der etwas anderes öffnet, machte dieselbe Geste in zwei Produkten
 * zu zwei verschiedenen Dingen (#158).
 *
 * Die laufende Fassung stand hier zwischen #135 und #158 in einer zweiten Zeile;
 * sie hängt jetzt am Benutzermenü (`UserFooter`), und von dort führt der einzige
 * Weg in die Neuerungen. Eingeklappt bleibt nur das Signet, und umbrechen darf
 * nichts.
 */
function LogoRow({ collapsed }: { collapsed: boolean }): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flex: "none",
        // Die Maße der Vorlage, unverändert: `margin: "6px 8px 0"`, Höhe 52,
        // `padding: 0 12px` und eingeklappt `0 9px` (`reference/AppShell.jsx`).
        marginTop: 6,
        marginLeft: 8,
        marginRight: 8,
        height: "var(--h-logo-row)",
        paddingLeft: collapsed ? 9 : 12,
        paddingRight: collapsed ? 9 : 12,
        borderRadius: "var(--r-popover)",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          flex: "none",
          width: 30,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Das Signet trägt sein `--ac-brand` in der Datei; ein `<img>` lässt sich
            von außen nicht einfärben. */}
        <img src={mark} width={24} height={24} alt="" />
      </span>

      {collapsed ? null : (
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "var(--fs-14)",
            fontWeight: "var(--fw-bold)",
            color: "var(--fg-1)",
            lineHeight: "var(--lh-tight)",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          SilverERP-Devtools
        </span>
      )}
    </div>
  );
}
